import type { Path } from '../Components/PathBuilder/pathBuilderTypes';
import type { PersistedState } from 'redux-persist';
import type { WebStorage } from 'redux-persist/es/types';
import type { PersistProjectMigrationIssue, PersistProjectMigrationReport } from './persistMigrationReport';
import { setPersistProjectMigrationReport } from './persistMigrationReport';
import { savePersistProjectBackupIfNeeded } from './persistProjectBackup';
import type { ProjectVersion } from './projectVersions';
import { applyProjectVersionDefaults, cloneProjectVersion, isProjectVersionAtLeast, pickProjectVersionFields } from './projectVersions';
import {
  applyCompactPathMigration,
  applyMetersCoordinateMigration,
  applySignedHeadingMigration,
  projectPathsNeedLegacyKeyMigration,
  projectPathsNeedMetersCoordinateMigration,
  projectPathsNeedSignedHeadingMigration,
  projectPathsNeedStructuralMigration,
} from './path-builder/path-segment-speeds';

/** Baseline project file version before schema migrations were tracked per feature. */
const LEGACY_PROJECT_VERSION: ProjectVersion = { major: 1, minor: 0, patch: 0 };

export type MigratableProject = {
  version?: ProjectVersion;
  paths?: Path[];
  pathBuilderFieldRotation?: number;
  selectedPathId?: string;
};

export type ProjectMigrationStep = {
  /** Stable migration identifier for tests, debugging, and future reporting. */
  id: string;
  /** Project file version after this step has been applied. */
  version: ProjectVersion;
  /**
   * When true while upgrading (project version &lt; step version), {@link migrate} runs.
   * When false, the step only stamps the new version (data already matches this step).
   * Never consulted for projects already at or above the step version.
   */
  needsStructuralMigration: (project: MigratableProject) => boolean;
  migrate: (project: MigratableProject) => MigratableProject;
};

function resolveProjectVersion(project: MigratableProject): ProjectVersion {
  return pickProjectVersionFields(project).version ?? LEGACY_PROJECT_VERSION;
}

/** True when upgrading to 1.0.2 should rewrite paths (signed headings, non-numeric compact fields, or leftover legacy keys). */
function projectNeedsSignedHeadingUpgrade(project: MigratableProject): boolean {
  return projectPathsNeedSignedHeadingMigration(project) || projectPathsNeedLegacyKeyMigration(project);
}

/**
 * Ordered project-file migrations. Append new entries here when the on-disk schema changes.
 *
 * Version policy:
 * - patch: backward-compatible normalization or compaction
 * - minor: new optional fields
 * - major: breaking schema changes
 *
 * Migrations run only when upgrading older project versions. Projects already at or above
 * a step's version are left unchanged (including hand-edited / future-schema files).
 */
export const PROJECT_MIGRATION_STEPS: readonly ProjectMigrationStep[] = [
  {
    id: 'compact-path-persistence',
    version: { major: 1, minor: 0, patch: 1 },
    needsStructuralMigration: projectPathsNeedStructuralMigration,
    migrate: applyCompactPathMigration,
  },
  {
    id: 'signed-waypoint-heading',
    version: { major: 1, minor: 0, patch: 2 },
    // May compact first when legacy keys remain on a mis-stamped 1.0.1 file; otherwise heading/numeric only.
    needsStructuralMigration: projectNeedsSignedHeadingUpgrade,
    migrate: applySignedHeadingMigration,
  },
  {
    id: 'path-builder-meters',
    version: { major: 1, minor: 0, patch: 3 },
    needsStructuralMigration: projectPathsNeedMetersCoordinateMigration,
    migrate: applyMetersCoordinateMigration,
  },
];

/** Latest project file version implied by {@link PROJECT_MIGRATION_STEPS}. */
export const LATEST_PROJECT_MIGRATION_VERSION: ProjectVersion =
  PROJECT_MIGRATION_STEPS[PROJECT_MIGRATION_STEPS.length - 1]?.version ?? LEGACY_PROJECT_VERSION;

function withProjectVersion<T extends MigratableProject>(project: T, version: ProjectVersion): T {
  return { ...project, version: cloneProjectVersion(version) };
}

/**
 * Applies every project migration the file has not yet reached, in order.
 * Does not re-run migrations for projects already at or above a step's version.
 */
export function migrateProject<T extends MigratableProject>(project: T): T {
  let next: MigratableProject = project;

  for (const step of PROJECT_MIGRATION_STEPS) {
    const currentVersion = resolveProjectVersion(next);
    if (isProjectVersionAtLeast(currentVersion, step.version)) {
      continue;
    }

    // Upgrading an older project through this step.
    if (step.needsStructuralMigration(next)) {
      next = step.migrate(next);
    }
    next = withProjectVersion(next, step.version);
  }

  return next as T;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object';
}

function migratePersistedProject(project: unknown): unknown {
  if (!isRecord(project)) return project;
  // Migrate before stamping defaults: missing `version` means legacy (1.0.0). Applying
  // CURRENT first would skip 1.0.1/1.0.2/1.0.3 upgrades for versionless persisted projects.
  return applyProjectVersionDefaults(migrateProject(project as Parameters<typeof migrateProject>[0]));
}

function readProjectLabel(project: Record<string, unknown>): { projectId?: string; projectName?: string } {
  const projectId = typeof project.id === 'string' ? project.id : undefined;
  const projectName = typeof project.name === 'string' ? project.name : undefined;
  return { projectId, projectName };
}

function migratePersistedProjectSafe(
  project: unknown,
  scope: PersistProjectMigrationIssue['scope']
): { project: unknown; issue?: PersistProjectMigrationIssue } {
  if (!isRecord(project)) {
    return { project };
  }

  try {
    return { project: migratePersistedProject(project) };
  } catch (error) {
    const { projectId, projectName } = readProjectLabel(project);
    return {
      project,
      issue: {
        scope,
        projectId,
        projectName,
        message: error instanceof Error ? error.message : String(error),
      },
    };
  }
}

export type PersistedProjectMigrationResult<T> = {
  state: T;
  issues: PersistProjectMigrationIssue[];
};

/** Normalizes persisted redux project state (versions + schema migrations) on rehydrate. */
export function migratePersistedProjectVersionState<T>(state: T): T {
  return migratePersistedProjectVersionStateSafe(state).state;
}

/**
 * Like {@link migratePersistedProjectVersionState} but isolates per-project failures
 * so one bad project cannot prevent the rest from migrating.
 */
export function migratePersistedProjectVersionStateSafe<T>(state: T): PersistedProjectMigrationResult<T> {
  if (!isRecord(state)) {
    return { state, issues: [] };
  }

  const candidate = state as Record<string, unknown>;
  const issues: PersistProjectMigrationIssue[] = [];

  let projects = candidate.projects;
  if (Array.isArray(candidate.projects)) {
    projects = candidate.projects.map((project, index) => {
      const result = migratePersistedProjectSafe(project, 'project');
      if (result.issue) {
        issues.push({
          ...result.issue,
          projectName: result.issue.projectName ?? `project ${index + 1}`,
        });
      }
      return result.project;
    });
  }

  let loadedProject = candidate.loadedProject;
  if (isRecord(candidate.loadedProject)) {
    const result = migratePersistedProjectSafe(candidate.loadedProject, 'loadedProject');
    if (result.issue) {
      issues.push(result.issue);
    }
    loadedProject = result.project;
  }

  return {
    state: {
      ...candidate,
      ...(Array.isArray(candidate.projects) && { projects }),
      ...(isRecord(candidate.loadedProject) && { loadedProject }),
    } as T,
    issues,
  };
}

function buildMigrationReport(
  issues: PersistProjectMigrationIssue[],
  catastrophicFailure: boolean,
  backupCreated: boolean
): PersistProjectMigrationReport {
  return {
    partialFailures: issues,
    catastrophicFailure,
    backupCreated,
  };
}

/**
 * redux-persist migrate hook: backs up raw state once, migrates safely, and never rejects
 * (returns the previous state on catastrophic failure so users do not lose local projects).
 */
export async function runSafePersistedProjectMigration(
  state: unknown,
  fromPersistVersion: number,
  storage: WebStorage,
  targetPersistVersion: number
): Promise<PersistedState> {
  let backupCreated = false;

  try {
    backupCreated = await savePersistProjectBackupIfNeeded(storage, fromPersistVersion, state, targetPersistVersion);
    const { state: migratedState, issues } = migratePersistedProjectVersionStateSafe(state);
    const report = buildMigrationReport(issues, false, backupCreated);
    if (hasMigrationReportIssues(report)) {
      setPersistProjectMigrationReport(report);
    }
    return migratedState as PersistedState;
  } catch (error) {
    const report = buildMigrationReport(
      [
        {
          scope: 'state',
          message: error instanceof Error ? error.message : String(error),
        },
      ],
      true,
      backupCreated
    );
    setPersistProjectMigrationReport(report);
    return (state ?? {}) as PersistedState;
  }
}

function hasMigrationReportIssues(report: PersistProjectMigrationReport): boolean {
  return report.catastrophicFailure || report.partialFailures.length > 0;
}
